import {useState} from 'react';
import {Dropdown, Form, Input, Menu, Table, Tag} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {toast} from 'react-toastify';
import dotsIcon from 'icons/dots.svg';
import DBModal from 'components/modal/modal';
import SubmitButton from 'components/Startup/components/SubmitButton';
import styles from './adminSettingsOverview.module.scss';
import {GET_ADMIN_DASHBOARD_MENU} from 'graphql/queries/adminDashboardMenu';
import {
  DELETE_ADMIN_DASHBOARD_MENU_SUBTAB,
  DELETE_ADMIN_DASHBOARD_MENU_TAB,
  UPSERT_ADMIN_DASHBOARD_MENU_SUBTAB,
  UPSERT_ADMIN_DASHBOARD_MENU_TAB,
} from 'graphql/mutations/adminDashboardMenu';

const MODAL = {
  CREATE_TAB: 'CREATE_TAB',
  EDIT_TAB: 'EDIT_TAB',
  DELETE_TAB: 'DELETE_TAB',
  CREATE_SUBTAB: 'CREATE_SUBTAB',
  EDIT_SUBTAB: 'EDIT_SUBTAB',
  DELETE_SUBTAB: 'DELETE_SUBTAB',
};

// Pull message off either union branch of the mutation response.
const readResult = payload => ({
  isError: payload?.__typename === 'Error' || payload?.statusCode === 400,
  message: payload?.message,
});

// Keys are derived from the label, never typed: tabs → UPPER_SNAKE_CASE,
// sub-tabs → kebab-case.
const toTabKey = label =>
  String(label || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');

const toSubTabKey = label =>
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

// Display the key without its separators (underscores / hyphens).
const formatKeyDisplay = key => String(key || '').replace(/[_-]/g, ' ');

const AdminDashboardMenuSettings = () => {
  const [form] = Form.useForm();
  const [modal, setModal] = useState({open: false});
  const labelValue = Form.useWatch('label', form);

  const {data, loading, refetch} = useQuery(GET_ADMIN_DASHBOARD_MENU, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const [upsertTab, {loading: savingTab}] = useMutation(
    UPSERT_ADMIN_DASHBOARD_MENU_TAB,
  );
  const [upsertSubTab, {loading: savingSubTab}] = useMutation(
    UPSERT_ADMIN_DASHBOARD_MENU_SUBTAB,
  );
  const [deleteTab, {loading: deletingTab}] = useMutation(
    DELETE_ADMIN_DASHBOARD_MENU_TAB,
  );
  const [deleteSubTab, {loading: deletingSubTab}] = useMutation(
    DELETE_ADMIN_DASHBOARD_MENU_SUBTAB,
  );

  const tabs = data?.getAdminDashboardMenu?.data || [];

  const closeModal = () => {
    setModal({open: false});
    form.resetFields();
  };

  const openModal = (type, payload = {}) => {
    setModal({open: true, type, ...payload});
    // Prefill the label on edits; the key is derived, never edited.
    if (type === MODAL.EDIT_TAB) {
      form.setFieldsValue({label: payload.tab?.label});
    } else if (type === MODAL.EDIT_SUBTAB) {
      form.setFieldsValue({label: payload.subTab?.label});
    } else {
      form.setFieldsValue({label: ''});
    }
  };

  const handleMutation = (promise, successMessage) =>
    promise
      .then(({data: result}) => {
        const payload = Object.values(result || {})[0];
        const {isError, message} = readResult(payload);
        if (isError) {
          toast.error(`Something went wrong!!! - ${message}`);
        } else {
          toast.success(successMessage);
          closeModal();
          refetch();
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err.message}`));

  const handleSubmitTab = values => {
    // On edit, keep the existing sub-tabs so a label change doesn't wipe them.
    const subTabs = (modal.tab?.subTabs || []).map(sub => ({
      key: sub.key,
      label: sub.label,
    }));
    // Key is derived from the label on create; never changes on rename.
    const key =
      modal.type === MODAL.EDIT_TAB ? modal.tab.key : toTabKey(values.label);
    handleMutation(
      upsertTab({
        variables: {
          data: {
            key,
            label: values.label.trim(),
            subTabs: modal.type === MODAL.EDIT_TAB ? subTabs : [],
          },
        },
      }),
      modal.type === MODAL.EDIT_TAB ? 'Tab updated' : 'Tab created',
    );
  };

  const handleSubmitSubTab = values => {
    const key =
      modal.type === MODAL.EDIT_SUBTAB
        ? modal.subTab.key
        : toSubTabKey(values.label);
    handleMutation(
      upsertSubTab({
        variables: {
          data: {
            tabKey: modal.tab.key,
            subTab: {key, label: values.label.trim()},
          },
        },
      }),
      modal.type === MODAL.EDIT_SUBTAB ? 'Sub-tab updated' : 'Sub-tab created',
    );
  };

  const handleDeleteTab = () =>
    handleMutation(deleteTab({variables: {key: modal.tab.key}}), 'Tab deleted');

  const handleDeleteSubTab = () =>
    handleMutation(
      deleteSubTab({
        variables: {tabKey: modal.tab.key, subTabKey: modal.subTab.key},
      }),
      'Sub-tab deleted',
    );

  const columns = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (text, record) => <span>{record.label}</span>,
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (text, record) => (
        <Tag color="blue">{formatKeyDisplay(record.key)}</Tag>
      ),
    },
    {
      title: 'Sub-tabs',
      dataIndex: 'subTabs',
      key: 'subTabs',
      render: (text, record) =>
        record.subTabs?.length ? (
          <span>{record.subTabs.length}</span>
        ) : (
          <span className={styles.muted}>Page-level</span>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, record) => (
        <Dropdown
          trigger="hover"
          overlay={
            <Menu>
              <Menu.Item
                key="add-sub"
                onClick={() => openModal(MODAL.CREATE_SUBTAB, {tab: record})}>
                Add Sub-tab
              </Menu.Item>
              <Menu.Item
                key="edit"
                onClick={() => openModal(MODAL.EDIT_TAB, {tab: record})}>
                Rename Tab
              </Menu.Item>
              <Menu.Item
                key="delete"
                onClick={() => openModal(MODAL.DELETE_TAB, {tab: record})}>
                Delete Tab
              </Menu.Item>
            </Menu>
          }>
          <img src={dotsIcon} onClick={e => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  const expandedRowRender = record =>
    record.subTabs?.length ? (
      <Table
        rowKey="key"
        pagination={false}
        dataSource={record.subTabs}
        columns={[
          {
            title: 'Sub-tab Label',
            dataIndex: 'label',
            key: 'label',
            render: (text, sub) => <span>{sub.label}</span>,
          },
          {
            title: 'Sub-tab Key',
            dataIndex: 'key',
            key: 'key',
            render: (text, sub) => <Tag>{formatKeyDisplay(sub.key)}</Tag>,
          },
          {
            title: 'Actions',
            key: 'actions',
            dataIndex: 'actions',
            render: (text, sub) => (
              <Dropdown
                trigger="hover"
                overlay={
                  <Menu>
                    <Menu.Item
                      key="edit-sub"
                      onClick={() =>
                        openModal(MODAL.EDIT_SUBTAB, {tab: record, subTab: sub})
                      }>
                      Rename Sub-tab
                    </Menu.Item>
                    <Menu.Item
                      key="delete-sub"
                      onClick={() =>
                        openModal(MODAL.DELETE_SUBTAB, {
                          tab: record,
                          subTab: sub,
                        })
                      }>
                      Delete Sub-tab
                    </Menu.Item>
                  </Menu>
                }>
                <img src={dotsIcon} onClick={e => e.stopPropagation()} />
              </Dropdown>
            ),
          },
        ]}
      />
    ) : (
      <span className={styles.muted}>No sub-tabs for this tab.</span>
    );

  const isTabModal =
    modal.type === MODAL.CREATE_TAB || modal.type === MODAL.EDIT_TAB;
  const isSubTabModal =
    modal.type === MODAL.CREATE_SUBTAB || modal.type === MODAL.EDIT_SUBTAB;
  const isDeleteModal =
    modal.type === MODAL.DELETE_TAB || modal.type === MODAL.DELETE_SUBTAB;

  return (
    <>
      <div className={styles.topButton}>
        <Button
          onClick={() => openModal(MODAL.CREATE_TAB)}
          className={styles.button}
          label="Add Tab"
        />
      </div>
      <Table
        rowKey="key"
        loading={loading}
        columns={columns}
        dataSource={tabs}
        expandable={{expandedRowRender}}
      />

      <DBModal
        isOpen={modal.open}
        handleClose={closeModal}
        content={
          <div className={styles.modalContainer}>
            {(isTabModal || isSubTabModal) && (
              <div className={styles.updateModal}>
                <h1 className={styles.modalTitle}>
                  {modal.type === MODAL.CREATE_TAB && 'Add Tab'}
                  {modal.type === MODAL.EDIT_TAB && 'Rename Tab'}
                  {modal.type === MODAL.CREATE_SUBTAB &&
                    `Add Sub-tab to ${modal.tab?.label}`}
                  {modal.type === MODAL.EDIT_SUBTAB && 'Rename Sub-tab'}
                </h1>
                <p className={styles.modalDescription}>
                  The key is generated automatically from the label —{' '}
                  {isTabModal ? 'UPPER_SNAKE_CASE' : 'kebab-case'}. On rename
                  the key stays the same so existing role grants keep working.
                </p>
                <Form
                  form={form}
                  layout="vertical"
                  className={styles.updateForm}
                  onFinish={isTabModal ? handleSubmitTab : handleSubmitSubTab}>
                  <Form.Item
                    name="label"
                    label="Label"
                    className={styles.formField}
                    rules={[{required: true, message: 'Label is required'}]}>
                    <Input placeholder="Display name" />
                  </Form.Item>
                  {modal.type === MODAL.CREATE_TAB ||
                  modal.type === MODAL.CREATE_SUBTAB ? (
                    <p className={styles.keyPreview}>
                      Key:{' '}
                      <strong>
                        {(isTabModal
                          ? toTabKey(labelValue)
                          : toSubTabKey(labelValue)) || '—'}
                      </strong>
                    </p>
                  ) : (
                    <p className={styles.keyPreview}>
                      Key:{' '}
                      <strong>
                        {isTabModal ? modal.tab?.key : modal.subTab?.key}
                      </strong>{' '}
                      (unchanged)
                    </p>
                  )}
                  <div className={styles.modalActions}>
                    <SubmitButton
                      label="Save"
                      disabled={savingTab || savingSubTab}
                      className={styles.updateButton}
                    />
                    <Button
                      label="Cancel"
                      type="secondary"
                      className={styles.cancelButton}
                      onClick={closeModal}
                    />
                  </div>
                </Form>
              </div>
            )}

            {isDeleteModal && (
              <div className={styles.deleteModal}>
                <h1 className={styles.modalTitle}>
                  {modal.type === MODAL.DELETE_TAB
                    ? 'Delete Tab'
                    : 'Delete Sub-tab'}
                </h1>
                <p className={styles.modalDescription}>
                  Are you certain you wish to delete{' '}
                  <strong>
                    {modal.type === MODAL.DELETE_TAB
                      ? modal.tab?.label
                      : modal.subTab?.label}
                  </strong>
                  ? This is a hard delete and{' '}
                  <strong>cannot be reversed</strong>. Roles that reference it
                  will lose this grant.
                </p>
                <div className={styles.modalActions}>
                  <Button
                    label="Delete"
                    className={styles.deleteButton}
                    disabled={deletingTab || deletingSubTab}
                    onClick={
                      modal.type === MODAL.DELETE_TAB
                        ? handleDeleteTab
                        : handleDeleteSubTab
                    }
                  />
                  <Button
                    label="Cancel"
                    type="secondary"
                    className={styles.cancelButton}
                    onClick={closeModal}
                  />
                </div>
              </div>
            )}
          </div>
        }
      />
    </>
  );
};

export default AdminDashboardMenuSettings;
